import { useState, useCallback, useMemo } from "react";
import { Hash, Library, SlidersHorizontal, Type } from "lucide-react";
import { useFontMixer, FONT_PRESETS, FontEntry } from "./FontMixerContext";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";

const COMMON_FONTS = [
    "monospace",
    "serif",
    "sans-serif",
    "Arial",
    "Times New Roman",
    "Courier New",
    "Georgia",
    "Verdana",
    "Helvetica",
    "Impact",
    "Comic Sans MS",
    "Trebuchet MS",
    "Tahoma",
    "Garamond",
    "Palatino Linotype",
    "Consolas",
    "Lucida Console",
    "Arial Black",
    "Brush Script MT",
    "Segoe Script",
    "Ink Free",
];

const FONT_WEIGHTS = [
    { value: 100, label: "100 Thin" },
    { value: 200, label: "200 Extra Light" },
    { value: 300, label: "300 Light" },
    { value: 400, label: "400 Regular" },
    { value: 500, label: "500 Medium" },
    { value: 600, label: "600 Semi Bold" },
    { value: 700, label: "700 Bold" },
    { value: 800, label: "800 Extra Bold" },
    { value: 900, label: "900 Black" },
];

const hasLocalFontAccess = "queryLocalFonts" in window;

export function FontMixerControls() {
    const {
        text,
        fontSize,
        selectedFonts,
        seed,
        letterSpacing,
        lineHeight,
        normalizeHeight,
        exportAsOutlines,
        activePreset,
        setText,
        setFontSize,
        setSelectedFonts,
        setSeed,
        setLetterSpacing,
        setLineHeight,
        setNormalizeHeight,
        setExportAsOutlines,
        setActivePreset,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = useFontMixer();

    const [systemFonts, setSystemFonts] = useState<string[] | null>(null);
    const [fontAccessError, setFontAccessError] = useState<string | null>(null);
    const [fontFilter, setFontFilter] = useState("");
    const [customFontInput, setCustomFontInput] = useState("");
    const [customFontWeight, setCustomFontWeight] = useState(400);

    const loadSystemFonts = useCallback(async () => {
        try {
            const fonts: FontData[] = await (window as any).queryLocalFonts();
            const families = new Set<string>();
            for (const f of fonts) {
                families.add(f.family);
            }
            const sorted = Array.from(families).sort((a, b) =>
                a.localeCompare(b, undefined, { sensitivity: "base" }),
            );
            setSystemFonts(sorted);
            setFontAccessError(null);
        } catch (err) {
            if (err instanceof DOMException && err.name === "NotAllowedError") {
                setFontAccessError("Permission denied.");
            } else {
                setFontAccessError("Failed to load fonts.");
            }
        }
    }, []);

    const allFontOptions = useMemo(() => {
        if (!systemFonts) return COMMON_FONTS;
        const commonSet = new Set(COMMON_FONTS);
        return [
            ...COMMON_FONTS,
            ...systemFonts.filter((f) => !commonSet.has(f)),
        ];
    }, [systemFonts]);

    const selectedFamilies = useMemo(
        () => new Set(selectedFonts.map((e) => e.family)),
        [selectedFonts],
    );

    const filteredFonts = useMemo(() => {
        const list = fontFilter.trim()
            ? allFontOptions.filter((f) =>
                  f.toLowerCase().includes(fontFilter.toLowerCase()),
              )
            : allFontOptions;
        return [
            ...list.filter((f) => selectedFamilies.has(f)),
            ...list.filter((f) => !selectedFamilies.has(f)),
        ];
    }, [allFontOptions, fontFilter, selectedFamilies]);

    const toggleFont = useCallback(
        (family: string) => {
            setSelectedFonts((prev) => {
                if (prev.some((e) => e.family === family)) {
                    return prev.filter((e) => e.family !== family);
                }
                return [...prev, { family, weight: 400 }];
            });
            setActivePreset("Custom");
        },
        [setSelectedFonts, setActivePreset],
    );

    const updateFontWeight = useCallback(
        (family: string, weight: number) => {
            setSelectedFonts((prev) =>
                prev.map((e) => (e.family === family ? { ...e, weight } : e)),
            );
            setActivePreset("Custom");
        },
        [setSelectedFonts, setActivePreset],
    );

    const handlePresetChange = useCallback(
        (presetLabel: string) => {
            setActivePreset(presetLabel);
            const preset = FONT_PRESETS.find((p) => p.label === presetLabel);
            if (preset && preset.fonts.length > 0) {
                setSelectedFonts(preset.fonts.map((f) => ({ ...f })));
            }
        },
        [setActivePreset, setSelectedFonts],
    );

    const addCustomFont = useCallback(() => {
        const name = customFontInput.trim();
        if (!name) return;
        setSelectedFonts((prev: FontEntry[]) => {
            if (
                prev.some(
                    (e) => e.family === name && e.weight === customFontWeight,
                )
            )
                return prev;
            return [...prev, { family: name, weight: customFontWeight }];
        });
        setActivePreset("Custom");
        setCustomFontInput("");
    }, [customFontInput, customFontWeight, setSelectedFonts, setActivePreset]);

    const handlePositiveNumber =
        (setter: (v: number) => void, min = 1, max = 500) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(n);
        };

    const selectAll = useCallback(() => {
        setSelectedFonts(
            allFontOptions.map((f) => ({ family: f, weight: 400 })),
        );
        setActivePreset("Custom");
    }, [allFontOptions, setSelectedFonts, setActivePreset]);

    const selectNone = useCallback(() => {
        setSelectedFonts([]);
        setActivePreset("Custom");
    }, [setSelectedFonts, setActivePreset]);

    return (
        <div className="panel-controls font-mixer-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Font Mixer</h2>
                    <ToolDescriptionPopover dialogLabel="Font Mixer tool description">
                        <p>
                            Type text and each character will be rendered with a
                            randomly selected font from the pool.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>
            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="font-mixer"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />

                <CollapsibleToolGroup title="Text" icon={Type}>
                    <div className="field-row">
                        <label className="field-label">
                            Text
                            <textarea
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                rows={4}
                                style={{ width: "100%", resize: "vertical" }}
                                placeholder="Type your text here..."
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Preset
                            <select
                                value={activePreset}
                                onChange={(e) =>
                                    handlePresetChange(e.target.value)
                                }
                            >
                                {FONT_PRESETS.map((p) => (
                                    <option key={p.label} value={p.label}>
                                        {p.label}
                                        {p.fonts.length > 0
                                            ? ` (${p.fonts.length})`
                                            : ""}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Font pool" icon={Library}>
                    <div className="field-row column">
                        <label className="field-label">
                            Font Pool ({selectedFonts.length} selected)
                        </label>
                        <div
                            style={{
                                display: "flex",
                                gap: "0.4rem",
                                marginBottom: "0.4rem",
                            }}
                        >
                            <button
                                type="button"
                                className="generic-button"
                                onClick={selectAll}
                                style={{
                                    fontSize: "0.75rem",
                                    padding: "0.2rem 0.5rem",
                                }}
                            >
                                All
                            </button>
                            <button
                                type="button"
                                className="generic-button"
                                onClick={selectNone}
                                style={{
                                    fontSize: "0.75rem",
                                    padding: "0.2rem 0.5rem",
                                }}
                            >
                                None
                            </button>
                        </div>
                        <input
                            type="text"
                            value={fontFilter}
                            onChange={(e) => setFontFilter(e.target.value)}
                            placeholder="Filter fonts..."
                            style={{ width: "100%", marginBottom: "0.4rem" }}
                        />
                        <div
                            className="app-scrollbar"
                            style={{
                                maxHeight: "200px",
                                overflowY: "auto",
                                border: "1px solid #555",
                                borderRadius: "4px",
                                padding: "0.3rem",
                            }}
                        >
                            {filteredFonts.map((f) => {
                                const entry = selectedFonts.find(
                                    (e) => e.family === f,
                                );
                                const isSelected = !!entry;
                                return (
                                    <div
                                        key={f}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.4rem",
                                            padding: "0.15rem 0.2rem",
                                        }}
                                    >
                                        <label
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "0.4rem",
                                                cursor: "pointer",
                                                fontFamily: f,
                                                flex: 1,
                                                minWidth: 0,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => toggleFont(f)}
                                            />
                                            <span
                                                style={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                                {f}
                                            </span>
                                        </label>
                                        {isSelected && (
                                            <select
                                                value={entry.weight}
                                                onChange={(e) =>
                                                    updateFontWeight(
                                                        f,
                                                        Number(e.target.value),
                                                    )
                                                }
                                                style={{
                                                    fontSize: "0.7rem",
                                                    padding: "0.1rem",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {FONT_WEIGHTS.map((w) => (
                                                    <option
                                                        key={w.value}
                                                        value={w.value}
                                                    >
                                                        {w.value}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                );
                            })}
                            {filteredFonts.length === 0 && (
                                <p style={{ margin: "0.3rem", opacity: 0.6 }}>
                                    No fonts match filter.
                                </p>
                            )}
                        </div>
                    </div>

                    {hasLocalFontAccess && !systemFonts && (
                        <div className="field-row">
                            <button
                                type="button"
                                className="generic-button"
                                onClick={loadSystemFonts}
                            >
                                Load system fonts
                            </button>
                        </div>
                    )}
                    {systemFonts && (
                        <p className="upload-info">
                            {systemFonts.length} system font
                            {systemFonts.length === 1 ? "" : "s"} loaded.
                        </p>
                    )}
                    {fontAccessError && (
                        <p className="upload-error">{fontAccessError}</p>
                    )}

                    <div className="field-row column">
                        <label className="field-label">Add Font</label>
                        <div
                            style={{
                                display: "flex",
                                gap: "0.4rem",
                                alignItems: "center",
                            }}
                        >
                            <input
                                type="text"
                                value={customFontInput}
                                onChange={(e) =>
                                    setCustomFontInput(e.target.value)
                                }
                                placeholder="Font family name"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") addCustomFont();
                                }}
                                style={{ flex: 1 }}
                            />
                            <select
                                value={customFontWeight}
                                onChange={(e) =>
                                    setCustomFontWeight(Number(e.target.value))
                                }
                                style={{ flexShrink: 0, fontSize: "0.8rem" }}
                            >
                                {FONT_WEIGHTS.map((w) => (
                                    <option key={w.value} value={w.value}>
                                        {w.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <button
                            type="button"
                            className="generic-button"
                            onClick={addCustomFont}
                            style={{ padding: "0.2rem 0.6rem", flexShrink: 0 }}
                        >
                            Add
                        </button>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Typography" icon={SlidersHorizontal}>
                    <div className="field-row">
                        <label className="field-label">
                            Font Size
                            <DebouncedInput
                                type="number"
                                min={8}
                                max={200}
                                step={1}
                                value={fontSize}
                                onChange={handlePositiveNumber(
                                    setFontSize,
                                    8,
                                    200,
                                )}
                                debounceMs={300}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Letter Spacing
                            <DebouncedInput
                                type="number"
                                min={-10}
                                max={50}
                                step={0.5}
                                value={letterSpacing}
                                onChange={(v) => {
                                    const n =
                                        typeof v === "number" ? v : Number(v);
                                    if (
                                        Number.isFinite(n) &&
                                        n >= -10 &&
                                        n <= 50
                                    )
                                        setLetterSpacing(n);
                                }}
                                debounceMs={300}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Line Height
                            <DebouncedInput
                                type="number"
                                min={0.5}
                                max={5}
                                step={0.1}
                                value={lineHeight}
                                onChange={(v) => {
                                    const n =
                                        typeof v === "number" ? v : Number(v);
                                    if (
                                        Number.isFinite(n) &&
                                        n >= 0.5 &&
                                        n <= 5
                                    )
                                        setLineHeight(n);
                                }}
                                debounceMs={300}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <CheckboxField
                            inline
                            checked={normalizeHeight}
                            onChange={setNormalizeHeight}
                            label="Normalize character height"
                        />
                    </div>

                    <div className="field-row">
                        <CheckboxField
                            inline
                            checked={exportAsOutlines}
                            onChange={setExportAsOutlines}
                            label="Export text as outlines"
                        />
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Seed" icon={Hash}>
                    <div className="field-row">
                        <label className="field-label">
                            Seed
                            <div
                                style={{
                                    display: "flex",
                                    gap: "0.4rem",
                                    alignItems: "center",
                                }}
                            >
                                <DebouncedInput
                                    type="number"
                                    min={0}
                                    max={999999}
                                    step={1}
                                    value={seed}
                                    onChange={handlePositiveNumber(
                                        setSeed,
                                        0,
                                        999999,
                                    )}
                                    debounceMs={300}
                                    style={{ flex: 1 }}
                                />
                                <button
                                    type="button"
                                    className="generic-button"
                                    onClick={() =>
                                        setSeed(
                                            Math.floor(Math.random() * 999999),
                                        )
                                    }
                                    style={{
                                        padding: "0.2rem 0.6rem",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    Randomize
                                </button>
                            </div>
                        </label>
                    </div>
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
